import { useEffect, useRef, useState } from "react";
import { Camera, IdCard, Info, Plus, Trash2 } from "lucide-react";

import {
  haalRassen,
  scanPaspoortDier,
  type AfstandDiersoort,
  type AfstandRas,
  type AfstandVraag,
} from "../../afstand/afstandApi";
import {
  compleetheid,
  diersoortKeuze,
  leeftijd,
  nieuwDier,
  rasKeuze,
  vragenCompleet,
  type Dier,
} from "../../afstand/afstandDieren";
import type { Bijlagen } from "./AfstandDocumentenStap";
import { AfstandEntingenboekje, LEEG_BOEKJE, type Boekje } from "./AfstandEntingenboekje";
import { AfstandVragenWizard } from "./AfstandVragenWizard";

/**
 * Stap 2: de dieren van deze aanvraag.
 *
 * <p><b>Eén dier is één eigen dossier.</b> Eigen basisgegevens, eigen vragen, eigen entingen, eigen
 * documenten. Alles hieronder werkt op het id van een dier en nooit op "het dier dat nu openstaat"
 * uit een gedeelde variabele — informatie van Bobby mag nooit bij Mimi terechtkomen.
 *
 * <p><b>Dieren kunnen verschillend ver zijn.</b> De kaartjes bovenaan laten dat zien, zodat je in
 * één oogopslag ziet waar nog werk ligt in plaats van het per dier te moeten nalopen.
 *
 * <p><b>Een dier weghalen vraagt om bevestiging.</b> Daar hangen antwoorden en documenten aan, en
 * een misklik mag die niet opruimen.
 */
export function AfstandDierenStap({ dieren, zetDieren, diersoorten, vragenVan, boekjes, zetBoekjes,
  opvangSleutel, bijlagen, zetBijlagen, verstuurdeFotos }: {
  dieren: Dier[];
  zetDieren: (d: Dier[]) => void;
  diersoorten: AfstandDiersoort[];
  /** De vragenlijst die bij een diersoort hoort; leeg zolang het asiel er geen heeft. */
  vragenVan: (diersoort: string) => AfstandVraag[];
  /** Het entingenboekje per dier-id; staat buiten dit scherm zodat het de stap overleeft. */
  boekjes: Record<string, Boekje>;
  zetBoekjes: (b: Record<string, Boekje>) => void;
  /** Nodig om de rassen van deze opvang op te halen. */
  opvangSleutel: string;
  /** De bijlagen van de hele aanvraag; de foto's van een dier horen daarbij. */
  bijlagen: Bijlagen;
  /**
   * De foto's die al bij het asiel liggen.
   *
   * <p>Zonder deze verdween een foto uit het beeld zodra hij verstuurd was: de wachtrij werd leeg
   * gemaakt en er stond niets voor in de plaats (Maarten, 27-10-2026). Leeg bij een aanvraag die
   * nog niet is ingediend -- dan ligt er nog niets.
   */
  verstuurdeFotos?: (dierId: string) => { documentId: string; naam: string; src: string | null }[];
  zetBijlagen: (b: Bijlagen) => void;
}) {
  const [open, setOpen] = useState<string | null>(dieren[0]?.id ?? null);
  const [wizardVoor, setWizardVoor] = useState<string | null>(null);
  const [weghalen, setWeghalen] = useState<string | null>(null);
  const [boekjeVoor, setBoekjeVoor] = useState<string | null>(null);
  const paspoort = useRef<HTMLInputElement>(null);
  const [leest, setLeest] = useState(false);
  const [uitlezing, setUitlezing] = useState<string | null>(null);

  /*
   * Alle rassen van het asiel, in één keer.
   *
   * Een vrij tekstveld leverde "Labra", "labrador" en "Labrador Retr." op; die moeten bij het
   * inboeken alsnog met de hand op een echt ras gelegd worden. Het asiel kent zijn eigen lijst, dus
   * die komt daarvandaan.
   *
   * Allemaal tegelijk en niet per diersoort: wie "Labrador" intikt weet wel dat het een hond is, en
   * hem eerst een keuzelijst laten afwerken om dat te bevestigen is werk dat dit formulier zelf kan
   * doen (Maarten, 26-10-2026). Het gaat om enkele honderden namen, dus dat kan gewoon ineens.
   */
  const [rassen, setRassen] = useState<AfstandRas[]>([]);

  useEffect(() => {
    let levend = true;
    void haalRassen(opvangSleutel)
      .then((lijst) => { if (levend) setRassen(lijst); })
      // Geen lijst is geen fout: dan is het rasveld gewoon een tekstveld, zoals het altijd al was.
      .catch(() => { if (levend) setRassen([]); });
    return () => { levend = false; };
  }, [opvangSleutel]);

  const huidig = dieren.find((d) => d.id === open) ?? null;
  const wizardDier = dieren.find((d) => d.id === wizardVoor) ?? null;

  /**
   * De foto's van een dier, in dezelfde bijlagenkaart als de documenten.
   *
   * <p>De sleutel is `<dier>:DIERFOTO`; daar leest het versturen uit bij welk dier ze horen en wat
   * voor soort bestand het is. Geen eigen opslag ernaast: twee plekken voor hetzelfde soort gegeven
   * lopen na de eerste wijziging uit elkaar.
   */
  const fotoSleutel = (dierId: string) => `${dierId}:DIERFOTO`;
  const fotosVan = (dierId: string) => bijlagen[fotoSleutel(dierId)] ?? [];

  /**
   * De foto's die al verstuurd zijn, plus de foto's die nog wachten.
   *
   * <p>Voor de eigenaar is dat één ding: de foto's van zijn dier. Dat de ene bij het asiel ligt en
   * de andere nog in dit tabblad staat is ons probleem, niet het zijne -- en hem alleen de wachtende
   * tonen betekende dat zijn foto bij het opslaan voor zijn ogen verdween (Maarten, 27-10-2026).
   */
  const alVerstuurd = (dierId: string) =>
    (verstuurdeFotos?.(dierId) ?? []).filter((f): f is typeof f & { src: string } => Boolean(f.src));
  const aantalFotos = (dierId: string) => alVerstuurd(dierId).length + fotosVan(dierId).length;
  const eersteFoto = (dierId: string) =>
    alVerstuurd(dierId)[0]?.src ?? fotoUrls.get(fotosVan(dierId)[0]?.bestand as File);

  /*
   * Een url per gekozen foto, zodat hij te zien is.
   *
   * Een bestandsnaam zegt niemand iets; je kiest een foto om te zien welke je hebt gekozen
   * (Maarten, 27-10-2026). De url hoort bij het bestand in het geheugen van dit tabblad en wordt
   * weer vrijgegeven zodra de foto weg is -- anders houdt een middag doorklikken alles vast.
   */
  const [fotoUrls, setFotoUrls] = useState<Map<File, string>>(new Map());

  useEffect(() => {
    const alle = Object.entries(bijlagen)
      .filter(([k]) => k.endsWith(":DIERFOTO"))
      .flatMap(([, v]) => v.map((b) => b.bestand));
    setFotoUrls((bestaand) => {
      const uit = new Map<File, string>();
      for (const bestand of alle) {
        uit.set(bestand, bestaand.get(bestand) ?? URL.createObjectURL(bestand));
      }
      for (const [bestand, url] of bestaand) {
        if (!uit.has(bestand)) URL.revokeObjectURL(url);
      }
      return uit;
    });
  }, [bijlagen]);

  /**
   * Foto's erbij.
   *
   * <p><b>Alleen afbeeldingen.</b> Wie hier een pdf of een tekstbestand kiest bedoelde dat niet; het
   * stilletjes aannemen levert bij het asiel een "foto" op die niets toont (Maarten, 27-10-2026).
   * Wat geweigerd wordt staat er met naam bij, want anders lijkt het alsof de keuze niet aankwam.
   */
  const voegFotosToe = (dierId: string, bestanden: File[]) => {
    const afbeeldingen = bestanden.filter((b) => b.type.startsWith("image/"));
    const geweigerd = bestanden.filter((b) => !b.type.startsWith("image/"));
    setGeweigerdeFotos(geweigerd.map((b) => b.name));
    if (afbeeldingen.length === 0) {
      return;
    }
    zetBijlagen({
      ...bijlagen,
      [fotoSleutel(dierId)]: [
        ...fotosVan(dierId),
        ...afbeeldingen.map((b) => ({ naam: b.name, bestand: b })),
      ],
    });
  };

  const [geweigerdeFotos, setGeweigerdeFotos] = useState<string[]>([]);

  const haalFotoWeg = (dierId: string, plek: number) =>
    zetBijlagen({
      ...bijlagen,
      [fotoSleutel(dierId)]: fotosVan(dierId).filter((_, i) => i !== plek),
    });

  const werkBij = (id: string, verandering: Partial<Dier>) =>
    zetDieren(dieren.map((d) => (d.id === id ? { ...d, ...verandering } : d)));

  /**
   * De voorkant van het paspoort laten lezen en de lege velden ermee vullen.
   *
   * <p>Alleen wat nog leeg is. Wat de eigenaar zelf heeft ingetikt blijft staan -- zijn invoer
   * overschrijven met een gok is erger dan niets invullen, want hij ziet het niet gebeuren.
   *
   * <p>Een diersoort of ras dat het asiel niet kent slaan we over: die velden zijn keuzelijsten, en
   * een waarde die er niet in staat zou een leeg vak opleveren dat wél gevuld lijkt.
   */
  const leesPaspoort = async (dier: Dier, bestanden: FileList | null) => {
    if (!bestanden || bestanden.length === 0) return;
    setLeest(true);
    setUitlezing(null);
    try {
      const uit = await scanPaspoortDier(opvangSleutel, Array.from(bestanden));
      if (!uit.beschikbaar) {
        setUitlezing("Automatisch uitlezen is nu niet beschikbaar. Vul de gegevens zelf in.");
        return;
      }

      const soort = uit.diersoort
        ? diersoorten.find((d) => d.naam.toLowerCase() === uit.diersoort!.toLowerCase())
        : undefined;
      const verandering: Partial<Dier> = {};
      if (!dier.naam && uit.naam) verandering.naam = uit.naam;
      if (!dier.chipPasnummer && uit.chipnummer) verandering.chipPasnummer = uit.chipnummer;
      if (!dier.geboortedatum && uit.geboortedatum) verandering.geboortedatum = uit.geboortedatum;
      if (dier.diersoortId == null && soort) {
        verandering.diersoortId = soort.id;
        verandering.diersoort = soort.naam;
      }
      if (!dier.ras && uit.ras) verandering.ras = uit.ras;
      if (!dier.geslacht && uit.geslacht) verandering.geslacht = uit.geslacht;

      const aantal = Object.keys(verandering).length;
      if (aantal === 0) {
        setUitlezing("We konden er niets uit halen wat nog niet ingevuld was.");
        return;
      }
      werkBij(dier.id, verandering);
      setUitlezing(`${aantal} veld(en) ingevuld. Controleer ze even; het blijft een uitlezing.`);
    } catch {
      setUitlezing("Het uitlezen lukte niet. Vul de gegevens zelf in.");
    } finally {
      setLeest(false);
    }
  };

  const voegToe = () => {
    const vers = nieuwDier(dieren.length + 1);
    zetDieren([...dieren, vers]);
    setOpen(vers.id);
  };

  return (
    <>
      <p className="afst__tip">
        <span className="afst__tipicoon" aria-hidden="true"><Info size={15} /></span>
        Eén afstandsaanvraag kan meerdere dieren bevatten. Voor elk dier vult u de gegevens en vragen
        afzonderlijk in.
      </p>

      <div className="afst__dierkaarten">
        {dieren.map((d, i) => {
          const vragen = vragenVan(d.diersoort);
          const pct = compleetheid(d, vragen);
          // Zonder vragenlijst valt er niets af te ronden; dan is "nog niet afgerond" geen
          // waarschuwing maar een raadsel.
          const af = vragen.length === 0 || (vragenCompleet(d, vragen) && d.vragenAfgerond);
          return (
            <button
              key={d.id}
              type="button"
              className={`afst__dierkaart${d.id === open ? " is-gekozen" : ""}`}
              onClick={() => setOpen(d.id)}
            >
              {/* De eerste foto als portret; zonder foto een leeg vlak, zoals voorheen. */}
              {eersteFoto(d.id) ? (
                <img className="afst__dierfoto" src={eersteFoto(d.id)} alt="" />
              ) : (
                <span className="afst__dierfoto" aria-hidden="true" />
              )}
              <span className="afst__dierinfo">
                <span className="afst__diernaam">
                  {d.naam || "Nieuw dier"}
                  <span className="afst__diernr">Dier {i + 1}</span>
                </span>
                <span className="afst__dierregel">
                  {[d.diersoort, d.geslacht, d.ras, leeftijd(d.geboortedatum)]
                    .filter(Boolean).join(" · ") || "Nog geen gegevens"}
                </span>
                <span className="afst__dierbalk" aria-hidden="true">
                  <span style={{ width: `${pct}%` }} />
                </span>
                <span className="afst__dierstand">
                  {pct}% ingevuld{af ? "" : " · Vragen nog niet afgerond"}
                </span>
              </span>
            </button>
          );
        })}

        <button type="button" className="afst__dierkaart afst__dierkaart--nieuw" onClick={voegToe}>
          <span className="afst__dierplus" aria-hidden="true"><Plus size={20} /></span>
          <span className="afst__dierinfo">
            <span className="afst__diernaam">Dier toevoegen</span>
            <span className="afst__dierregel">Ook voor andere dieren binnen deze aanvraag</span>
          </span>
        </button>
      </div>

      {huidig && (
        <section className="afst__paneel afst__dierpaneel">
          <div className="afst__paspoortrij">
            <div>
              <strong>Gegevens overnemen uit het paspoort</strong>
              <p>Maak een foto van de identificatiebladzijde; wij vullen in wat we kunnen lezen.</p>
            </div>
            <button type="button" className="afst__knop afst__knop--rustig" disabled={leest}
                    onClick={() => paspoort.current?.click()}>
              <IdCard size={18} aria-hidden="true" />
              {leest ? "Bezig met uitlezen…" : "Foto van het paspoort"}
            </button>
            <input
              ref={paspoort}
              type="file"
              hidden
              multiple
              accept="image/*"
              capture="environment"
              onChange={(e) => { void leesPaspoort(huidig, e.target.files); e.target.value = ""; }}
            />
          </div>
          {uitlezing && <p className="afst__melding">{uitlezing}</p>}

          <header className="afst__dierkop">
            <h3>
              Dier {dieren.findIndex((d) => d.id === huidig.id) + 1} · {huidig.naam || "Nieuw dier"}
              <small>Basisgegevens en dossier</small>
            </h3>
            {vragenVan(huidig.diersoort).length > 0
              && !(huidig.vragenAfgerond && vragenCompleet(huidig, vragenVan(huidig.diersoort))) && (
              <span className="afst__waarschuwing">Afstandsvragen: nog niet afgerond</span>
            )}
          </header>

          <div className="afst__raster">
            <Tekstveld label="Roepnaam *" waarde={huidig.naam}
                       zet={(v) => werkBij(huidig.id, { naam: v })} />
            <label className="afst__veld afst__veld--derde">
              <span>Diersoort *</span>
              <span className="afst__invoer">
                <select
                  value={huidig.diersoortId ?? ""}
                  onChange={(e) => {
                    const gekozen = diersoorten.find((s) => String(s.id) === e.target.value);
                    // Naam én id bewaren: het id gaat naar het asiel, de naam kiest de vragenlijst.
                    // Een ras van een andere soort gaat eruit; zie diersoortKeuze waarom.
                    werkBij(huidig.id, diersoortKeuze(gekozen, huidig, rassen));
                  }}
                >
                  <option value="">Maak een keuze</option>
                  {diersoorten.map((s) => <option key={s.id} value={s.id}>{s.naam}</option>)}
                </select>
              </span>
            </label>
            <label className="afst__veld afst__veld--derde">
              <span>Ras</span>
              {/*
                * Intikken en kiezen tegelijk: een tekstveld met de rassen van het asiel eronder.
                *
                * Geen keuzelijst die eerst een diersoort eist -- je mag hier gewoon "Labrador" typen.
                * Is de diersoort al gekozen, dan tonen we alleen de rassen van die soort; dat scheelt
                * zoeken en voorkomt dat er een katras bij een hond komt te staan.
                *
                * Vrij intikken blijft mogelijk, want een kruising of een zeldzame soort staat niet in
                * de lijst. Dan gaat alleen de tekst mee en vult het asiel het ras bij het inboeken in.
                */}
              <span className="afst__invoer">
                <input
                  value={huidig.ras}
                  list={`rassen-${huidig.id}`}
                  placeholder="Typ of kies een ras"
                  onChange={(e) => werkBij(huidig.id, rasKeuze(e.target.value, rassen, diersoorten))}
                />
              </span>
              <datalist id={`rassen-${huidig.id}`}>
                {rassen
                  .filter((r) => huidig.diersoortId == null || r.diersoortId === huidig.diersoortId)
                  .map((r) => <option key={r.id} value={r.naam} />)}
              </datalist>
            </label>

            <label className="afst__veld afst__veld--derde">
              <span>Geslacht *</span>
              <span className="afst__invoer">
                <select value={huidig.geslacht} onChange={(e) => werkBij(huidig.id, { geslacht: e.target.value })}>
                  <option value="">Maak een keuze</option>
                  {["Reu", "Teef", "Mannelijk", "Vrouwelijk", "Onbekend"].map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </span>
            </label>
            <label className="afst__veld afst__veld--derde">
              <span>Geboortedatum / leeftijd *</span>
              <span className="afst__invoer">
                <input type="date" value={huidig.geboortedatum}
                       onChange={(e) => werkBij(huidig.id, { geboortedatum: e.target.value })} />
              </span>
            </label>
            <Tekstveld label="Chipnummer" waarde={huidig.chipPasnummer}
                       hint="15 cijfers, of leeg als het dier geen chip heeft"
                       zet={(v) => werkBij(huidig.id, { chipPasnummer: v })} />
          </div>

          {/*
            * De knop verdwijnt wanneer het asiel voor deze diersoort niets heeft ingericht.
            *
            * Een grijze knop onder de belofte van "vier korte stappen" laat iemand klikken op iets
            * dat nooit opengaat, en dan denkt hij dat het portaal stuk is. Er staat dan gewoon dat
            * er geen vragen zijn -- dat is namelijk waar, en het is geen fout van hem.
            *
            * Hoeveel stappen het er zijn hangt van de vragenlijst af; daarom staat het aantal er
            * niet meer als vast getal in.
            */}
          <div className="afst__dierrij">
            <div>
              <strong>Afstandsvragen voor {huidig.naam || "dit dier"}</strong>
              <p>
                {!huidig.diersoort
                  ? "Kies eerst een diersoort; daarna weten we welke vragen het asiel stelt."
                  : vragenVan(huidig.diersoort).length === 0
                    ? "Voor deze diersoort stelt het asiel geen extra vragen."
                    : "Een paar korte stappen over de situatie, gezondheid, gedrag en verzorging."}
              </p>
            </div>
            {vragenVan(huidig.diersoort).length > 0 && (
              <button
                type="button"
                className="afst__knop"
                /* Zonder diersoort is niet te zeggen welke vragen het asiel wil stellen. */
                disabled={!huidig.diersoort}
                title={!huidig.diersoort ? "Kies eerst een diersoort" : undefined}
                onClick={() => setWizardVoor(huidig.id)}
              >
                Afstandsvragen invullen
              </button>
            )}
          </div>

          {/*
            * Foto's van het dier zelf.
            *
            * <p>Het asiel beoordeelt een dier dat het nog nooit heeft gezien; een foto zegt daar meer
            * over dan drie regels tekst (Maarten, 26-10-2026). Ze gaan als bijlage mee, net als de
            * documenten -- hier bij het dier en niet in de documentenstap, want hier zit je er al.
            */}
          <div className="afst__dierrij">
            <div>
              <strong>Foto's van {huidig.naam || "dit dier"}</strong>
              <p>
                {aantalFotos(huidig.id) > 0
                  ? `${aantalFotos(huidig.id)} foto('s) toegevoegd.`
                  : "Een foto zegt het asiel meer over uw dier dan een beschrijving."}
              </p>
            </div>
            <span className="afst__fotoknop">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const gekozen = Array.from(e.target.files ?? []);
                  if (gekozen.length > 0) voegFotosToe(huidig.id, gekozen);
                  // Leegmaken, anders kan dezelfde foto niet nog eens gekozen worden.
                  e.target.value = "";
                }}
              />
              <span className="afst__knop afst__knop--rustig">
                <Camera size={18} aria-hidden="true" />
                {aantalFotos(huidig.id) > 0 ? "Meer foto's" : "Foto's toevoegen"}
              </span>
            </span>
          </div>

          {geweigerdeFotos.length > 0 && (
            <p className="afst__melding afst__melding--let-op" role="alert">
              {geweigerdeFotos.join(", ")} {geweigerdeFotos.length === 1 ? "is geen" : "zijn geen"}
              {" "}afbeelding en {geweigerdeFotos.length === 1 ? "is" : "zijn"} niet toegevoegd.
              Een dierenpaspoort of verslag hoort bij de documenten.
            </p>
          )}

          {aantalFotos(huidig.id) > 0 && (
            <ul className="afst__fotogalerij">
              {/* Wat al bij het asiel ligt heeft geen prullenbak: die zou een knop zijn die niets
                  mag. Het asiel haalt een foto weg als hij er niet hoort. */}
              {alVerstuurd(huidig.id).map((f) => (
                <li key={f.documentId}>
                  <img src={f.src} alt={f.naam} />
                  <span className="afst__fotomerk">Verstuurd</span>
                </li>
              ))}
              {fotosVan(huidig.id).map((f, i) => (
                <li key={`${f.naam}-${i}`}>
                  <img src={fotoUrls.get(f.bestand)} alt={f.naam} />
                  <button type="button" onClick={() => haalFotoWeg(huidig.id, i)}
                          aria-label={`${f.naam} verwijderen`}>
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="afst__dierrij">
            <div>
              <strong>Entingenboekje</strong>
              <p>
                {boekjes[huidig.id]?.gecontroleerd
                  ? `${boekjes[huidig.id].entingen.length} vaccinatie(s) gecontroleerd en opgeslagen.`
                  : "Maak foto's van het boekje; controleer de automatisch herkende gegevens."}
              </p>
            </div>
            <button type="button" className="afst__knop afst__knop--rustig"
                    onClick={() => setBoekjeVoor(huidig.id)}>
              <Camera size={18} aria-hidden="true" />
              {(boekjes[huidig.id]?.paginas.length ?? 0) > 0
                ? `Foto's bekijken (${boekjes[huidig.id].paginas.length})`
                : "Foto's toevoegen"}
            </button>
          </div>

          {dieren.length > 1 && (
            <button type="button" className="afst__weghalen" onClick={() => setWeghalen(huidig.id)}>
              <Trash2 size={16} aria-hidden="true" />
              Dit dier uit de aanvraag halen
            </button>
          )}
        </section>
      )}

      {wizardDier && (
        <AfstandVragenWizard
          dierNaam={wizardDier.naam || "dit dier"}
          vragen={vragenVan(wizardDier.diersoort)}
          antwoorden={wizardDier.antwoorden}
          onBewaar={(antwoorden, afgerond) =>
            werkBij(wizardDier.id, { antwoorden, vragenAfgerond: afgerond || wizardDier.vragenAfgerond })}
          onSluit={() => setWizardVoor(null)}
        />
      )}

      {boekjeVoor && (
        <AfstandEntingenboekje
          dierNaam={dieren.find((d) => d.id === boekjeVoor)?.naam || "dit dier"}
          opvangSleutel={opvangSleutel}
          boekje={boekjes[boekjeVoor] ?? LEEG_BOEKJE}
          zetBoekje={(b) => zetBoekjes({ ...boekjes, [boekjeVoor]: b })}
          onSluit={() => setBoekjeVoor(null)}
        />
      )}

      {weghalen && (
        <div className="afst__venster" role="dialog" aria-modal="true" aria-label="Dier weghalen">
          <div className="afst__venster-blad afst__venster-blad--klein">
            <header className="afst__venster-kop">
              <div>
                <h3>Dit dier uit de aanvraag halen?</h3>
                <p>
                  De gegevens, antwoorden en documenten van dit dier gaan weg. Dat kan niet ongedaan
                  worden gemaakt.
                </p>
              </div>
            </header>
            <footer className="afst__venster-voet">
              <button type="button" className="afst__knop afst__knop--rustig"
                      onClick={() => setWeghalen(null)}>
                Laten staan
              </button>
              <button type="button" className="afst__knop afst__knop--gevaar"
                      onClick={() => {
                        const over = dieren.filter((d) => d.id !== weghalen);
                        zetDieren(over);
                        // Het boekje gaat mee: anders blijven er foto's hangen van een dier dat
                        // niet meer in de aanvraag zit.
                        const rest = { ...boekjes };
                        delete rest[weghalen];
                        zetBoekjes(rest);
                        setOpen(over[0]?.id ?? null);
                        setWeghalen(null);
                      }}>
                Weghalen
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}

function Tekstveld({ label, waarde, zet, hint }: {
  label: string;
  waarde: string;
  zet: (v: string) => void;
  hint?: string;
}) {
  return (
    <label className="afst__veld afst__veld--derde">
      <span>{label}</span>
      <span className="afst__invoer">
        <input value={waarde} placeholder={hint} onChange={(e) => zet(e.target.value)} />
      </span>
    </label>
  );
}
